import React from "react";
import Panel from "../../components/panels/panel";
import { Modal } from "../../components";
import { usePagination } from "../../utilities/hookutils";
import NewEmailModal, { type EmailState } from "../create/NewEmailModal";
import EmailRowCard from "./EmailRow";
import { emailApis } from "utilities/apiUtils";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";

interface Props {
  mainArea: server.dto.MainAreas
  emailTemplateType: server.dto.MessageTemplateTypes
  config: server.dto.LinkedEmailPanelConfig
}

const EmailsPanel = (props: Props) => {
  const [emails, setEmails] = React.useState<server.dto.EmailMessage[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [displayNewEmail, setDisplayNewEmail] = React.useState(false);

  const getDefaultEmailState = () => ({
    charactersRemaining: serverReference.constants.emailMessage.maxBodyLength,
    selectedTemplateId: undefined,
    recipientsTo: props.config.defaultTo || "",
    recipientsCC: "",
    recipientsBCC: props.config.userEmailProfile.defaultBcc || "",
    subject: "",
    htmlContent: props.config.userEmailProfile.signature || "",
    visibleToExternal: props.config.defaultVisibleToExternal || false,
    selectedFiles: [],
    downloadLinkInEmail: false,
    includeAttachmentsWhenSending: false,
    attachmentRequired: false,
    hasAttemptedToSend: false,
    isSending: false,
    emailError: undefined
  });

  const [newEmail, setNewEmail] = React.useState<EmailState>(getDefaultEmailState());

  const onEmailChange = (partial: Partial<EmailState>) => {
    setNewEmail(prevEmail => ({ ...prevEmail, ...partial }));
  };

  const parentId = props.config.itemId;

  const emailApi = emailApis[props.mainArea];

  const paginate = async (paginationData: Partial<server.dto.PaginationData>) => {
    const getEmailResult = await emailApi.Api.PaginatedEmails({ parentId, page: paginationData.page, perPage: paginationData.perPage });

    setEmails(getEmailResult.data.data);
    setLoading(false);

    return getEmailResult.data.pagination;
  };

  const onDelete = async (email: server.dto.EmailMessage) => {
    await window.CoreApi.EmailMessage.DeleteEmailMessage(email.emailMessageId);
    return pagination.onPaginate(pagination.page);
  };

  const onEmailSent = () => {
    setDisplayNewEmail(false);
    pagination.onPaginate(pagination.page);
    setNewEmail(getDefaultEmailState());
  };

  const pagination = usePagination(10, paginate);

  const { refToScrollTo } = useShortcutPanel({
    type: "email",
    label: props.config.headerLabel,
    totalItems: emails.length,
    onAddClicked: props.config.canSend ? () => { setDisplayNewEmail(true); } : null
  });

  return <>
    <Panel
      panelClassName="s1_related-feature-panel"
      button={props.config.canSend && <ButtonSecondary onClick={() => { setDisplayNewEmail(true); }} label={RESX.Buttons.btnNew} />}
      panelRef={refToScrollTo}
      title={props.config.headerLabel}
      pagination={pagination.Component}
      isLoading={loading}
      hasData={emails.length > 0}
    >
      <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {emails.map(e => <EmailRowCard key={e.emailMessageId} email={e} onDelete={onDelete} canDelete={props.config.canDelete} />)}
        </div>
      </div>
    </Panel>
    <Modal
      isOpen={displayNewEmail}
      handleClose={() => { setDisplayNewEmail(false); }}
      title={RESX.OptionsPanel.NewEmail}
      width="large"
      hasAutoScrollBox
    >
      <NewEmailModal
        mainArea={props.mainArea}
        emailTemplateType={props.emailTemplateType}
        config={props.config}
        onMailSent={onEmailSent}
        emailState={newEmail}
        onEmailStateChanged={onEmailChange}
      />
    </Modal>
  </>;
};

export default EmailsPanel;